import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext"; 
import { useTheme } from "../context/ThemeContext"; 
import ProductCard from "../components/ProductCard";
import UserTopbar from "../components/UserTopbar"; 
import adminAxios from "../admin/adminAxios"; // Isolated tenant-aware client replacing standard axios

function Products() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const userId = user?._id; 
  const { orderUpdated } = useSocket(); 
  const { colors } = useTheme(); 

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  // Retrieve isolated tenant context identifier matching admin and Home views
  const tenantId = localStorage.getItem("adminId") || localStorage.getItem("tenantId") || "global";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchProducts = useCallback(async () => {
    if (authLoading) return;
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await adminAxios.get(`/orders/user/${userId}`, { params: { tenantId } });
      
      if (!response.data || !Array.isArray(response.data)) {
        setProducts([]);
      } else {
        const assigned = response.data
          .filter(o => o.status === "assigned")
          .map(order => ({
            ...order,
            orderId: order._id,
            image: order.image || (order.productId ? order.productId.image : null),
            name: order.name || (order.productId ? order.productId.name : "Asset Manifest"),
            details: order.details || (order.productId ? order.productId.details : "Standard Logistics Unit")
          }));
        setProducts(assigned);
      }
    } catch (err) {
      console.error("[System] Tenant Sync Error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, authLoading, tenantId]);

  const handleAssignmentSuccess = useCallback(() => {
    fetchProducts();
    setTimeout(() => {
        navigate("/orders");
    }, 800);
  }, [fetchProducts, navigate]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (orderUpdated) {
      fetchProducts();
    }
  }, [orderUpdated, fetchProducts]);

  if (authLoading) {
    return (
      <div style={{...styles.loaderContainer, backgroundColor: colors.bg}} data-tenant-scope={tenantId}>
        <div style={{...styles.spinner, borderTopColor: colors.brand, borderLeftColor: colors.border}}></div>
        <p style={{marginTop: '20px', fontWeight: '700', letterSpacing: '2px', color: colors.brand}}>VERIFYING NODE CREDENTIALS...</p>
      </div>
    );
  }

  return (
    <div style={{...styles.layoutStyle, backgroundColor: colors.bg}} data-tenant-scope={tenantId}>
      {/* INJECTED NEW TOPBAR */}
      <UserTopbar title="" />

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{...styles.headerBadge, backgroundColor: colors.brand}}>PENDING AUTHORIZATION : {products.length}</div>
          <p style={{...styles.subtitle, color: colors.muted}}>
            Review and approve assigned cargo manifests. Authorized units will be transferred to active transit for final delivery.
          </p>
        </div>

        {loading ? (
          <div style={{...styles.statusMsg, color: colors.muted}}>
             <span className="pulse">●</span> SCANNING LOGISTICS DATABASE...
          </div>
        ) : products.length === 0 ? (
          <div style={{...styles.emptyState, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
            <div style={{ fontSize: "48px", marginBottom: "15px" }}>⚓</div>
            <p style={{ fontSize: "18px", fontWeight: "900", color: colors.brand, letterSpacing: '1px' }}>QUEUE CLEAR</p>
            <p style={{ color: colors.muted, marginTop: "8px", fontSize: "12px", fontWeight: '600' }}>
              No assigned manifests found. Add items to cart from the Dashboard to see them here.
            </p>
          </div>
        ) : (
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(280px, 1fr))",
            gap: isMobile ? "12px" : "24px" 
          }}>
            {products.map((p) => (
              <div key={p._id} className="assigned-card-wrapper">
                <ProductCard
                  product={p} 
                  highlight={true}
                  showPickButton={true}
                  onSuccess={handleAssignmentSuccess} 
                  customCardStyle={{
                    padding: isMobile ? "12px" : "18px",
                    borderRadius: "18px",
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  }}
                  buttonStyle={{ 
                    background: colors.brand, 
                    borderRadius: '8px',
                    fontSize: isMobile ? '11px' : '13px',
                    padding: '12px',
                    fontWeight: '700',
                    border: 'none',
                    marginTop: '15px',
                    textTransform: 'uppercase',
                    width: '100%',
                    color: '#fff'
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .pulse { color: ${colors.brand}; margin-right: 10px; animation: blink 1.5s infinite; }
        @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
        
        .assigned-card-wrapper img {
          height: ${isMobile ? '140px' : '190px'} !important;
          border-radius: 12px !important;
          object-fit: fill !important;
          background: #000;
        }

        .assigned-card-wrapper h3 {
          color: ${colors.text} !important;
          font-family: 'Inter', sans-serif;
          font-size: ${isMobile ? '13px' : '15px'} !important;
          font-weight: 700 !important;
          margin-top: 10px !important;
        }

        .assigned-card-wrapper p {
            color: ${colors.muted} !important;
            font-size: 11px !important;
        }

        .assigned-card-wrapper:hover {
            transform: translateY(-4px);
            border-color: ${colors.brand} !important;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  layoutStyle: { 
    minHeight: "100vh", 
    paddingTop: "180px", 
    paddingBottom: "100px", 
    fontFamily: "'Inter', sans-serif",
    transition: "0.3s ease background-color"
  },
  container: { padding: "0 20px", maxWidth: "1100px", margin: "0 auto" },
  header: { marginBottom: "35px" },
  headerBadge: { 
    display: 'inline-block', color: '#fff', 
    padding: '6px 12px', borderRadius: '8px', fontSize: '10px', fontWeight: '900', marginBottom: '12px' 
  },
  subtitle: { margin: "0", fontSize: "13px", fontWeight: "500", lineHeight: "1.6", maxWidth: "700px" },
  statusMsg: { textAlign: "center", padding: "100px 0", fontSize: "12px", fontWeight: "800", letterSpacing: '2px' },
  emptyState: { 
    textAlign: "center", padding: "100px 20px", borderRadius: "28px"
  },
  loaderContainer: { 
    display: 'flex', flexDirection: 'column', alignItems: 'center', 
    justifyContent: 'center', height: '100vh'
  },
  spinner: { 
    width: '40px', height: '40px', border: `4px solid transparent`, 
    borderRadius: '50%', animation: 'spin 1s linear infinite' 
  }
};

export default Products;